import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { isAxiosError } from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { citysprintApis } from '../../../apis';
import { CITYSPRINT_BASE_URL } from '../../../constants/env';
import useCustomSnackbar from '../../../features/CustomSnackbar/useSnackbar';
import { useOverlay } from '../../../features/Overlay';
import { STRINGS } from '../../../constants';
import { SEND_A_CITYSPRINT_PARCEL } from '../../../constants/routes';
import { StepsList } from '../../Shipment/components/StepsList';
import {
  CITYSPRINT_CONFIRMATION,
  CITYSPRINT_INNER_PANEL_DARK_BG,
} from '../constants';
import { CitysprintWizardActions } from '../redux';

function formatTimeWindow(earliestIso, latestIso) {
  if (!earliestIso) {
    return '—';
  }
  try {
    const start = new Date(earliestIso);
    const end = latestIso ? new Date(latestIso) : start;
    const opts = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    };
    if (start.getTime() === end.getTime()) {
      return start.toLocaleString('en-GB', opts);
    }
    return `${start.toLocaleString('en-GB', opts)} – ${end.toLocaleString(
      'en-GB',
      opts
    )}`;
  } catch {
    return String(earliestIso);
  }
}

function isOrderMissingOrForbidden(err) {
  const status = isAxiosError(err) ? err.response?.status : undefined;
  return status === 404 || status === 403;
}

// eslint-disable-next-line complexity -- small function
function CitysprintBookingSuccess() {
  const theme = useTheme();
  const { bookingId: bookingIdParam } = useParams();
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showError } = useCustomSnackbar();
  const overlay = useOverlay();
  const showErrorRef = useRef(showError);
  const navigateRef = useRef(navigate);
  showErrorRef.current = showError;
  navigateRef.current = navigate;

  useEffect(() => {
    dispatch(CitysprintWizardActions.clear());
  }, [dispatch]);

  const invoiceOobCode = useMemo(() => {
    const raw = locationState?.invoiceOobCode;
    return raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
  }, [locationState]);

  const [booking, setBooking] = useState(null);
  const [fetchState, setFetchState] = useState(() =>
    bookingIdParam ? 'loading' : 'idle'
  );

  const decodedId = useMemo(() => {
    if (!bookingIdParam) {
      return '';
    }
    try {
      return decodeURIComponent(bookingIdParam);
    } catch {
      return bookingIdParam;
    }
  }, [bookingIdParam]);

  const stepsConfig = useMemo(
    () => [
      {
        id: 1,
        icon: Inventory2OutlinedIcon,
        text: CITYSPRINT_CONFIRMATION.STEP_COURIER,
      },
      {
        id: 2,
        icon: LocalShippingOutlinedIcon,
        text: CITYSPRINT_CONFIRMATION.STEP_TRACK,
      },
      {
        id: 3,
        icon: SupportAgentOutlinedIcon,
        text: CITYSPRINT_CONFIRMATION.STEP_HELP,
      },
    ],
    []
  );

  useLayoutEffect(() => {
    if (!bookingIdParam) {
      navigate(SEND_A_CITYSPRINT_PARCEL, { replace: true });
    }
  }, [bookingIdParam, navigate]);

  useEffect(() => {
    if (!bookingIdParam) {
      return undefined;
    }
    let cancelled = false;
    const id = decodedId;

    async function load() {
      setFetchState('loading');
      setBooking(null);
      try {
        const response = await citysprintApis.getOrderById(id);
        const envelope = response?.data ?? response;
        const payload = envelope?.data ?? envelope;
        if (cancelled) {
          return;
        }
        setBooking(
          payload && typeof payload === 'object'
            ? {
                bookingNumber: payload.bookingNumber,
                trackingURL: payload.trackingURL,
                collection: payload.collection,
                delivery: payload.delivery,
                vehicleDescription: payload.vehicleDescription,
                serviceDescription: payload.serviceDescription,
              }
            : null
        );
        setFetchState('success');
      } catch (err) {
        if (cancelled) {
          return;
        }
        setBooking(null);
        if (isOrderMissingOrForbidden(err)) {
          showErrorRef.current({
            message: CITYSPRINT_CONFIRMATION.ORDER_NOT_FOUND,
          });
          navigateRef.current(SEND_A_CITYSPRINT_PARCEL, { replace: true });
          return;
        }
        setFetchState('error');
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [bookingIdParam, decodedId]);

  useEffect(() => {
    if (fetchState === 'loading') {
      overlay.show();
    } else {
      overlay.hide();
    }
    return () => {
      overlay.hide();
    };
  }, [fetchState, overlay.show, overlay.hide]);

  const handleTrack = () => {
    const url = booking?.trackingURL;
    if (url && typeof url === 'string') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadInvoice = useCallback(() => {
    if (!invoiceOobCode) {
      showErrorRef.current({
        message: CITYSPRINT_CONFIRMATION.INVOICE_DOWNLOAD_FAILED,
      });
      return;
    }
    const pdfUrl = `${CITYSPRINT_BASE_URL.replace(
      /\/$/,
      ''
    )}/citysprint/orders/invoice?oobCode=${encodeURIComponent(invoiceOobCode)}`;
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  }, [invoiceOobCode]);

  if (!bookingIdParam) {
    return null;
  }

  const bookingNumber =
    booking?.bookingNumber != null ? String(booking.bookingNumber) : null;
  const vehicleDescription =
    typeof booking?.vehicleDescription === 'string' &&
    booking.vehicleDescription.trim()
      ? booking.vehicleDescription.trim()
      : null;
  const serviceDescription =
    typeof booking?.serviceDescription === 'string' &&
    booking.serviceDescription.trim()
      ? booking.serviceDescription.trim()
      : null;
  const collectionLabel = formatTimeWindow(
    booking?.collection?.earliestTime,
    booking?.collection?.latestTime
  );
  const deliveryLabel = formatTimeWindow(
    booking?.delivery?.earliestTime,
    booking?.delivery?.latestTime
  );
  const hasFullDetails = Boolean(
    fetchState === 'success' && booking && bookingNumber
  );
  const canTrack = Boolean(booking?.trackingURL);

  return (
    <Grid
      sx={{
        background: theme.vars.palette.primary.pageBackground,
        flexGrow: 1,
        pb: 4,
      }}
    >
      <Grid container alignItems='center' justifyContent='center'>
        <Grid
          alignItems='center'
          justifyContent='center'
          spacing={2}
          sx={{ mt: 0 }}
        >
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <Grid
              sx={{
                m: 4,
                marginX: 0,
                paddingX: 2,
                maxWidth: 400,
              }}
            >
              <Typography variant='body1'>
                {STRINGS.CONFIRMATION.THANK_YOU}
              </Typography>
              <Typography variant='h2' component='h1' sx={{ mt: 1 }}>
                {STRINGS.CONFIRMATION.ALL_DONE}
              </Typography>
            </Grid>

            <Grid sx={{ mt: 1 }}>
              <Paper sx={{ p: 0, maxWidth: '400px' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='h3' component='h2'>
                    {CITYSPRINT_CONFIRMATION.CARD_TITLE}
                  </Typography>
                </Box>
                <Divider />

                <Box sx={{ px: 2, py: 2 }}>
                  {hasFullDetails ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {CITYSPRINT_CONFIRMATION.BOOKING_REFERENCE}
                        </Typography>
                        <Typography variant='body1'>{bookingNumber}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {CITYSPRINT_CONFIRMATION.VEHICLE}
                        </Typography>
                        <Typography variant='body1'>
                          {vehicleDescription ?? '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {CITYSPRINT_CONFIRMATION.SERVICE}
                        </Typography>
                        <Typography variant='body1'>
                          {serviceDescription ?? '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {CITYSPRINT_CONFIRMATION.COLLECTION_WINDOW}
                        </Typography>
                        <Typography variant='body1'>
                          {collectionLabel}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 0 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {CITYSPRINT_CONFIRMATION.DELIVERY_WINDOW}
                        </Typography>
                        <Typography variant='body1'>{deliveryLabel}</Typography>
                      </Box>
                    </>
                  ) : null}
                  {!hasFullDetails && fetchState !== 'loading' ? (
                    <Typography variant='body2' color='text.secondary'>
                      {CITYSPRINT_CONFIRMATION.NO_STATE_HINT}{' '}
                      <Typography
                        component='span'
                        variant='body2'
                        fontWeight={600}
                      >
                        {decodedId}
                      </Typography>
                    </Typography>
                  ) : null}
                </Box>

                <Divider />

                <Grid
                  sx={theme => ({
                    background: theme.vars.palette.primary.contrastText,
                    [theme.getColorSchemeSelector('dark')]: {
                      background: CITYSPRINT_INNER_PANEL_DARK_BG,
                    },
                  })}
                >
                  <Grid container alignItems='center' justifyContent='center'>
                    <Grid
                      size={{
                        xs: 12,
                        md: 12,
                      }}
                    >
                      <Box sx={{ px: 2, pt: 2, pb: 0 }}>
                        <Typography variant='subtitle2' component='p'>
                          {CITYSPRINT_CONFIRMATION.WHAT_NEXT}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <StepsList config={stepsConfig} />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Divider />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    px: 1,
                  }}
                >
                  <Button
                    variant='contained'
                    color='primary'
                    disabled={!canTrack}
                    onClick={handleTrack}
                    sx={{ mx: 1, mb: 0, mt: 1 }}
                  >
                    {CITYSPRINT_CONFIRMATION.TRACK_PARCEL}
                  </Button>
                  <Button
                    variant='outlined'
                    color='primary'
                    disabled={!hasFullDetails || !invoiceOobCode}
                    onClick={handleDownloadInvoice}
                    sx={{ mx: 1, mb: 0, mt: 1 }}
                  >
                    {CITYSPRINT_CONFIRMATION.DOWNLOAD_INVOICE}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={SEND_A_CITYSPRINT_PARCEL}
                    replace
                    sx={{
                      mx: 1,
                      mb: 2,
                      mt: 1,
                      color: theme.palette.primary.main,
                    }}
                    variant='outlined'
                  >
                    {CITYSPRINT_CONFIRMATION.SEND_ANOTHER_PARCEL}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default CitysprintBookingSuccess;
